/**
 * Settings → Data model → a class → "Edit details…": the display name of a discovered data model and what the admins
 * know about it (description, domain, tags, steward, sensitivity), and whether it has been checked. The window is a
 * step in history (`?details=<class>`, lib/urlmodal.ts); what is typed is kept in sessionStorage until it is saved.
 */
import { useMemo, useState, type KeyboardEvent } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Api, ApiError } from '../lib/api.ts';
import { useFormDraft } from '../lib/formstate.ts';
import { pluralOf } from '../lib/nouns.ts';
import { CATALOG_LIMITS, SENSITIVITIES, SENSITIVITY_LABEL, confidenceWord, formOf, formProblems, hasSuggestions, parseTags, patchOf, suggestions, type CatalogEntry } from '../lib/catalog.ts';
import { SuggestedTag } from './Suggested.tsx';
import { IconButton, cx } from '../design/primitives.tsx';
import { CloseIcon } from './icons.tsx';
import { Button, Field, inputClass } from './ui.tsx';
import { useSystems } from '../lib/systemsdata.tsx';
import { useAsync } from '../lib/hooks.ts';
import { resolveSystem, systemName } from '../lib/systems.ts';

export const catalogDraftKey = (tid: string, cls: string) => `catalog.${tid}.${cls}`;

/** Small buttons under a field that fill in a value already in use. */
function Suggest({ items, onPick, label }: { items: string[]; onPick: (v: string) => void; label: string }) {
  if (items.length === 0) return null;
  return (
    <div className="flex items-center gap-1.5 flex-wrap" role="group" aria-label={label}>
      {items.map((s) => (
        <button key={s} type="button" onClick={() => onPick(s)}
          className="h-6 px-2.5 rounded-full border-0 bg-n3 text-micro text-n9 cursor-pointer hover:bg-n4 hover:text-n10 transition-colors duration-[var(--t-micro)]">{s}</button>
      ))}
    </div>
  );
}

/** What the automatic categorization suggests for an empty field: the value, how sure, why, and Accept (fills the field). */
function Suggestion({ label, value, confidence, reasons, personal, onAccept }: {
  label: string; value: string; confidence?: number | null; reasons: string[]; personal?: boolean; onAccept: () => void;
}) {
  return (
    <div className={cx('flex flex-col gap-0.5 rounded-chip border border-dashed px-2.5 py-1.5', personal ? 'border-warn bg-warn-soft' : 'border-n5')} aria-label={`Suggested ${label}`}>
      <div className="flex items-center gap-2 text-small min-w-0">
        <SuggestedTag className={personal ? 'text-warn' : 'text-n7'} />
        <span className={cx('font-medium truncate', personal ? 'text-warn' : 'text-n9')}>{value}</span>
        {confidence != null && <span className="text-micro text-n7 whitespace-nowrap">{confidenceWord(confidence)}</span>}
        <button type="button" onClick={onAccept} className="ml-auto h-6 px-2 rounded-chip border-0 bg-transparent text-micro font-medium text-accent cursor-pointer hover:bg-n3">Accept</button>
      </div>
      {reasons.length > 0 && <span className="text-micro text-n8 [overflow-wrap:anywhere]">{reasons.join(' · ')}</span>}
    </div>
  );
}

export function CatalogEditor({ tid, entry, domains, tags, onClose, onSaved }: {
  tid: string; entry: CatalogEntry; domains: string[]; tags: string[];
  onClose: () => void; onSaved: (e: CatalogEntry) => void;
}) {
  const [f, set, clear] = useFormDraft(catalogDraftKey(tid, entry.class), formOf(entry));
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string>();
  const [domainFocus, setDomainFocus] = useState(false);
  const problems = formProblems(f);
  const bad = Object.keys(problems).length > 0;
  const name = f.displayName.trim() || entry.derived.displayName;
  const pluralHint = f.displayName.trim() && f.displayName.trim() !== entry.derived.displayName ? pluralOf(f.displayName.trim()) : entry.derived.displayPlural;
  const addTags = (text: string) => set({ tags: parseTags(text, f.tags), tagText: '' });
  const tagKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'Enter' || e.key === ',') && f.tagText.trim()) { e.preventDefault(); addTags(f.tagText); }
    else if (e.key === 'Backspace' && !f.tagText && f.tags.length) set({ tags: f.tags.slice(0, -1) });
  };
  const save = async () => {
    if (bad) return;
    setBusy(true);
    setFailure(undefined);
    try {
      const saved = await Api.updateCatalog(tid, entry.class, patchOf(f, entry));
      clear();
      onSaved(saved);
    } catch (e) {
      setFailure(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  };
  // The system: pinned here, or as the data says (the Cloud's detection, docs/systems.md)
  const { systems } = useSystems();
  const found = useAsync(() => Api.systems(tid), [tid]);
  const detected = found.data?.classes[entry.class]?.detected ?? null;
  const systemOptions = useMemo(() => [...systems.values()].filter((x) => x.status !== 'hidden' || x.code === f.system)
    .sort((a, b) => a.name.localeCompare(b.name)), [systems, f.system]);
  const systemHint = detected ? `Detected: ${systemName(detected)} (${resolveSystem(detected)})` : found.loading ? 'Looking at the data…' : 'Not detected yet';
  const domainSuggestions = domainFocus || f.domain ? suggestions(domains, f.domain, [], 6) : [];
  const tagSuggestions = suggestions(tags, f.tagText, f.tags, 8);
  // The suggestions (docs/catalog.md): placeholders of the empty fields, each with Accept
  const sug = entry.suggestions;
  const sugDomain = sug?.domain && !f.domain.trim() ? sug.domain : null;
  const sugSensitivity = sug?.sensitivity && !f.sensitivity ? sug.sensitivity : null;
  const sugTags = sug && f.tags.length === 0 && !f.tagText.trim() ? sug.tags : [];

  return (
    <Dialog.Root open onOpenChange={(open) => { if (!open) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px]" />
        <Dialog.Content aria-describedby={undefined}
          className="fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(640px,94vw)] max-h-[90vh] overflow-auto flex flex-col gap-4 bg-n2 text-n10 rounded-card shadow-float p-5 outline-none mv-pop">
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0 flex flex-col gap-0.5">
              <Dialog.Title className="m-0 text-body font-semibold">Details of {name}</Dialog.Title>
              <span className="text-micro text-n7 font-mono [overflow-wrap:anywhere]">{entry.class}</span>
            </div>
            <Dialog.Close asChild><IconButton label="Close"><CloseIcon size={16} /></IconButton></Dialog.Close>
          </div>
          <form className="flex flex-col gap-4" noValidate onSubmit={(e) => { e.preventDefault(); void save(); }}>
            <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
              <Field label="Display name" error={problems.displayName} hint="As said in a sentence">
                <input className={inputClass} value={f.displayName} placeholder={entry.derived.displayName} maxLength={CATALOG_LIMITS.name + 20} autoFocus
                  onChange={(e) => set({ displayName: e.target.value })} aria-invalid={!!problems.displayName} />
              </Field>
              <Field label="Plural" error={problems.displayPlural} hint={f.displayPlural ? undefined : `Empty: “${pluralHint}”`}>
                <input className={inputClass} value={f.displayPlural} placeholder={pluralHint} maxLength={CATALOG_LIMITS.name + 20}
                  onChange={(e) => set({ displayPlural: e.target.value })} aria-invalid={!!problems.displayPlural} />
              </Field>
            </div>
            {(f.displayName || f.displayPlural) && (
              <div className="-mt-2">
                <button type="button" onClick={() => set({ displayName: '', displayPlural: '' })}
                  className="h-7 px-2.5 -ml-2.5 rounded-chip border-0 bg-transparent text-small text-accent cursor-pointer hover:bg-n3">
                  Use derived: {entry.derived.displayName} / {entry.derived.displayPlural}
                </button>
              </div>
            )}
            <Field label="Description" error={problems.description}>
              <textarea className={cx(inputClass, 'h-auto min-h-24 py-2 resize-y leading-relaxed')} rows={4} value={f.description}
                placeholder="What this data is, where it comes from, what it is used for" onChange={(e) => set({ description: e.target.value })} aria-invalid={!!problems.description} />
            </Field>
            <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Field label="Domain" error={problems.domain}>
                  <input className={inputClass} value={f.domain} placeholder={sug?.domain ? `${sug.domain} (suggested)` : 'e.g. Sales'} onChange={(e) => set({ domain: e.target.value })}
                    onFocus={() => setDomainFocus(true)} aria-invalid={!!problems.domain} />
                </Field>
                {sugDomain && <Suggestion label="domain" value={sugDomain} confidence={sug!.confidence.domain} reasons={sug!.reasons.domain} onAccept={() => set({ domain: sugDomain })} />}
                <Suggest label="Domains in use" items={domainSuggestions} onPick={(v) => set({ domain: v })} />
              </div>
              <Field label="Steward" error={problems.steward} hint="Who answers for this data">
                <input className={inputClass} value={f.steward} placeholder="Name or team" onChange={(e) => set({ steward: e.target.value })} aria-invalid={!!problems.steward} />
              </Field>
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="flex flex-col gap-1 text-small">
                <span className="text-n8">Tags</span>
                <span className="min-h-9 w-full rounded-chip bg-n3 px-1.5 py-1 flex items-center gap-1 flex-wrap focus-within:ring-[1.5px] focus-within:ring-accent">
                  {f.tags.map((t) => (
                    <span key={t} className="inline-flex items-center gap-1 h-7 pl-2.5 pr-1 rounded-full bg-n2 text-small text-n10 shadow-node">
                      {t}
                      <button type="button" aria-label={`Remove tag ${t}`} onClick={() => set({ tags: f.tags.filter((x) => x !== t) })}
                        className="size-5 grid place-items-center rounded-full border-0 bg-transparent text-n7 cursor-pointer hover:bg-n3 hover:text-n10"><CloseIcon size={12} /></button>
                    </span>
                  ))}
                  <input className="flex-1 min-w-32 h-7 bg-transparent border-0 outline-none px-1.5 text-small text-n10 placeholder:text-n7" value={f.tagText}
                    placeholder={f.tags.length ? 'Add a tag' : sug?.tags.length ? `${sug.tags.join(', ')} (suggested)` : 'Type a tag, Enter to add'} aria-label="Add a tag"
                    onChange={(e) => (e.target.value.includes(',') ? addTags(e.target.value) : set({ tagText: e.target.value }))}
                    onKeyDown={tagKey} onBlur={() => { if (f.tagText.trim()) addTags(f.tagText); }} />
                </span>
                {problems.tags && <span className="text-micro text-fail">{problems.tags}</span>}
              </div>
              {sugTags.length > 0 && <Suggestion label="tags" value={sugTags.join(', ')} reasons={sug!.reasons.tags} onAccept={() => set({ tags: parseTags(sugTags.join(','), f.tags), tagText: '' })} />}
              <Suggest label="Tags in use" items={tagSuggestions} onPick={(v) => set({ tags: parseTags(v, f.tags), tagText: '' })} />
            </div>
            <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
              <Field label="Sensitivity">
                <select className={inputClass} value={f.sensitivity} onChange={(e) => set({ sensitivity: e.target.value })}>
                  <option value="">{sug?.sensitivity ? `Not set (suggested: ${SENSITIVITY_LABEL[sug.sensitivity]})` : 'Not set'}</option>
                  {SENSITIVITIES.map((s) => <option key={s} value={s}>{SENSITIVITY_LABEL[s]}</option>)}
                </select>
              </Field>
              <Field label="System" hint={f.system ? `Pinned · ${systemHint.toLowerCase()}` : systemHint}>
                <select className={inputClass} value={f.system} onChange={(e) => set({ system: e.target.value })}>
                  <option value="">As detected{detected ? `: ${systemName(detected)}` : ''}</option>
                  {systemOptions.map((x) => <option key={x.code} value={x.code}>{x.name} ({x.code})</option>)}
                </select>
              </Field>
            </div>
            {sugSensitivity && (
              <Suggestion label="sensitivity" value={`${SENSITIVITY_LABEL[sugSensitivity]}${sug!.fields.personal.length && sugSensitivity === 'personal' ? `: ${sug!.fields.personal.slice(0, 5).join(', ')}` : ''}`}
                personal={sugSensitivity === 'personal'} confidence={sug!.confidence.sensitivity} reasons={sug!.reasons.sensitivity} onAccept={() => set({ sensitivity: sugSensitivity })} />
            )}
            {failure && <p className="m-0 text-small text-fail">{failure}</p>}
            <div className="flex items-center gap-3 flex-wrap pt-1">
              {entry.status === 'new' && (
                <label className="flex items-center gap-2 text-small text-n9 cursor-pointer mr-auto">
                  <input type="checkbox" className="size-4 cursor-pointer accent-[var(--accent)]" checked={f.markChecked} onChange={(e) => set({ markChecked: e.target.checked })} />
                  {hasSuggestions(entry) && (sugDomain || sugSensitivity || sugTags.length) ? 'Mark as checked and accept the suggestions left empty' : 'Mark as checked'}
                </label>
              )}
              <span className={entry.status === 'new' ? '' : 'flex-1'} />
              <Button variant="ghost" onClick={() => { clear(); onClose(); }}>Cancel</Button>
              <Button variant="primary" type="submit" disabled={busy || bad}>{busy ? 'Saving…' : 'Save'}</Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
